import { useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { Segmentation } from '@core';
import { useGraphData } from '@hooks';
import { useTranslate } from '@i18n';
import { useUiGraph } from '@queries';
import {
    activeAnalysisAtom,
    activeUserSettingsAtom,
    comparisonAnalysisAtom,
    currentFrameAtom,
    currentPositionNumberAtom,
} from '@store';
import { Box, colors, FloorGraph as FloorGraphUI, Spinner } from '@ui';

import { ModuleBaseFloor } from '../ModuleBase';

import { GraphProps } from './Graph';

type FloorGraphProps = Omit<GraphProps, 'options'> & {
    omitNodes: string[] | undefined;
};

export const FloorGraph = ({ nodeId, omitNodes }: FloorGraphProps) => {
    const userSettings = useAtomValue(activeUserSettingsAtom);
    const currentFrame = useAtomValue(currentFrameAtom);
    const activeAnalysis = useAtomValue(activeAnalysisAtom);
    const selectedAnalysis = activeAnalysis?.fullAnalysis ?? activeAnalysis?.quickAnalysis;
    const comparison = useAtomValue(comparisonAnalysisAtom);
    const comparisonAnalysis = comparison?.fullAnalysis ?? comparison?.quickAnalysis;
    const activePosition = useAtomValue(currentPositionNumberAtom);

    const { data: modules } = useUiGraph();

    const graphNode = useMemo(() => modules?.find((node) => node.id === nodeId), [modules, nodeId]);

    const { graphData, segmentation, graphLines, omitLines } = useGraphData({
        nodeId,
        userPreferredUnit: userSettings.userPreferredUnit,
        selectedAnalysis,
        comparisonAnalysis,
        omitNodes,
    });
    const t = useTranslate();
    const nodeName = t(graphNode?.i18n ?? '');
    const isLoading = !graphData || !graphLines;

    return (
        <ModuleBaseFloor title={nodeName}>
            {!isLoading ? (
                <FloorGraphUI
                    data={graphData}
                    lines={graphLines}
                    segmentation={segmentation as { current: Segmentation; comparison: Segmentation | null }}
                    currentFrame={currentFrame}
                    activePosition={activePosition}
                    showComparison={userSettings.showComparison}
                    omitLines={omitLines}
                />
            ) : (
                <Box display="flex" height="100%" justifyContent="center" alignItems="center">
                    <Spinner size="l" color={colors.bluegray[600]} />
                </Box>
            )}
        </ModuleBaseFloor>
    );
};
